import React from "react";
import PropTypes from "prop-types";
import { Box, Link, Typography, useTheme } from "@mui/material";
import PersonCardV2 from "./PersonCardV2";
import { SIDEBAR } from "./sidebarVisual";

const ROW_HEIGHT = 44;
const HEADER_HEIGHT = 44;

const columnsSx = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1.1fr) minmax(90px, 0.7fr)",
  columnGap: 0,
  alignItems: "center",
  px: SIDEBAR.cardPadX,
  boxSizing: "border-box",
};

function personDisplayName(person) {
  return person.fullName || person.name || [person.firstName, person.lastName].filter(Boolean).join(" ") || "—";
}

function personEmail(person) {
  return person.email || person.emails?.[0] || null;
}

function personPhone(person) {
  return person.phone || person.telephone || null;
}

function CellText({ children, secondary = false, sx: sxProp }) {
  return (
    <Typography
      component="span"
      sx={{
        fontSize: 13,
        fontWeight: secondary ? 400 : 500,
        color: secondary ? "text.secondary" : "text.primary",
        lineHeight: 1.2,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...sxProp,
      }}
    >
      {children}
    </Typography>
  );
}

CellText.propTypes = {
  children: PropTypes.node,
  secondary: PropTypes.bool,
  sx: PropTypes.object,
};

/**
 * People list as a Search-style spreadsheet table (44px rows, header band, stripes).
 * On compact/mobile, renders stacked PersonCardV2 rows instead.
 */
export default function PeopleTableV2({ people = [], compact = false }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const headerBg = isDark ? "#2A2A2A" : "#F9FAFB";
  const stripeBg = isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)";
  const divider = theme.palette.divider;

  if (compact) {
    return (
      <Box
        data-testid="people-table-v2"
        data-compact="true"
        sx={{ display: "flex", flexDirection: "column", gap: SIDEBAR.gap.listItem }}
      >
        {people.map((person, index) => {
          const rowKey = person.email || person.name || person.fullName || index;
          return (
            <PersonCardV2
              key={rowKey}
              fullName={person.fullName}
              name={person.name}
              title={person.title}
              email={person.email}
              emails={person.emails}
              phone={person.phone}
              telephone={person.telephone}
              imageUrl={person.imageUrl}
            />
          );
        })}
      </Box>
    );
  }

  return (
    <Box
      data-testid="people-table-v2"
      sx={{
        width: "100%",
        borderRadius: SIDEBAR.radius,
        border: SIDEBAR.border,
        overflow: "hidden",
        bgcolor: SIDEBAR.surface,
        boxShadow: SIDEBAR.shadow,
      }}
    >
      <Box
        role="row"
        aria-label="People column headers"
        sx={{
          ...columnsSx,
          flexShrink: 0,
          minHeight: HEADER_HEIGHT,
          maxHeight: HEADER_HEIGHT,
          backgroundColor: headerBg,
          color: "text.secondary",
          borderBottom: `1px solid ${divider}`,
        }}
      >
        {["Name", "Title", "Email", "Phone"].map((label) => (
          <Typography
            key={label}
            component="span"
            sx={{ fontWeight: 700, fontSize: 13, color: "inherit", lineHeight: 1.2 }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      <Box>
        {people.map((person, index) => {
          const name = personDisplayName(person);
          const title = person.title || "—";
          const email = personEmail(person);
          const phone = personPhone(person);
          const rowKey = person.email || person.name || person.fullName || index;

          return (
            <Box
              key={rowKey}
              role="row"
              data-testid={`people-table-row-${rowKey}`}
              sx={{
                ...columnsSx,
                minHeight: ROW_HEIGHT,
                height: ROW_HEIGHT,
                maxHeight: ROW_HEIGHT,
                overflow: "hidden",
                cursor: "default",
                borderBottom: `1px solid ${divider}`,
                backgroundColor:
                  index % 2 === 1 ? stripeBg : theme.palette.background.paper,
                "&:last-child": {
                  borderBottom: "none",
                },
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CellText sx={{ fontWeight: 550 }}>{name}</CellText>
              <CellText secondary={title === "—"}>{title}</CellText>
              {email ? (
                <Link
                  href={`mailto:${email}`}
                  underline="hover"
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "primary.main",
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {email}
                </Link>
              ) : (
                <CellText secondary>—</CellText>
              )}
              <CellText secondary={!phone}>{phone || "—"}</CellText>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

PeopleTableV2.propTypes = {
  people: PropTypes.arrayOf(PropTypes.object),
  compact: PropTypes.bool,
};
